import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { format } from "date-fns";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { useQuery } from "@tanstack/react-query";

const reportsResource = new ReportsResource(newApInstance);
export const AttendantsReportController = () => {
  const { isMobile } = useDimensions();

  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const filterModalRef = useRef<IModalRefProps>(null);

  const { data: attendantsResponse, isFetching: isFetchingAttendants } =
    useQuery({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        "attendants",
        filterStartDate,
        filterEndDate,
        selectedStore,
        selectedProduct,
        selectedAttendant
      ],
      queryFn: () =>
        reportsResource.getAttendantsReports({
          startDate: format(filterStartDate, "yyyy-MM-dd"),
          endDate: format(filterEndDate, "yyyy-MM-dd"),
          store: selectedStore,
          product: selectedProduct,
          attendant: selectedAttendant
        })
    });
  const columnHelper = createColumnHelper();

  const headerColumns = attendantsResponse?.data.table.headers.map((header) => {
    return columnHelper.accessor(header.key, {
      header: () => (
        <Text
          fontSize="$xs"
          color={header?.styles?.text?.color || "$textLight700"}
          fontWeight={header?.styles?.text?.fontWeight || 600}
        >
          {header.label}
        </Text>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      meta: header?.styles
    });
  });

  const { tableData, hasReportData } = useMemo(() => {
    const tableData = convertDataRows(
      attendantsResponse?.data?.table.headers,
      attendantsResponse?.data?.table.rows
    );

    const hasReportData =
      Array.isArray(attendantsResponse?.data?.table.rows) &&
      attendantsResponse.data.table.rows.length > 0;

    return {
      tableData,
      hasReportData
    };
  }, [attendantsResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      "Relatório_Atendentes",
      attendantsResponse?.data.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    filterModalRef,
    topRightText,
    filterStartDate,
    filterEndDate,
    hasReportData,
    handleSelectFilters,
    isFetchingAttendants
  };
};
